import React from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { authBaseUrl } from './authApiConfig'
import { useTranslation } from '@structura/i18n'

const ResetPassword: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') ?? ''

  const [password, setPassword] = React.useState<string>('')
  const [confirmPassword, setConfirmPassword] = React.useState<string>('')
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)
  const [success, setSuccess] = React.useState<boolean>(false)
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)

  const passwordsMatch = password === confirmPassword
  const passwordValid = password.length >= 6

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    setErrorMessage(null)

    if (!passwordsMatch) {
      setErrorMessage(t('auth.reset.errMismatch'))
      return
    }

    if (!passwordValid) {
      setErrorMessage(t('auth.reset.errMinChars'))
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch(`${authBaseUrl}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password })
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => undefined)
        const message: string = (payload !== undefined && typeof payload.error === 'string')
          ? payload.error
          : t('auth.reset.errReset')
        setErrorMessage(message)
        return
      }

      setSuccess(true)
      setTimeout(() => {
        navigate('/account')
      }, 3000)
    } catch {
      setErrorMessage(t('auth.common.networkError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (token.length === 0) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="card border-0 shadow-lg" style={{ borderRadius: '1rem' }}>
              <div className="card-body p-5 text-center">
                <div 
                  className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                  style={{ width: '70px', height: '70px', background: 'rgba(220, 53, 69, 0.1)' }}
                >
                  <i className="bi bi-exclamation-triangle" style={{ fontSize: '2rem', color: 'crimson' }}></i>
                </div>
                <h2 className="fw-bold mb-3">{t('auth.reset.invalidLink')}</h2>
                <p className="text-muted mb-4">
                  {t('auth.reset.invalidLinkText')}
                </p>
                <Link to="/forgot-password" className="btn" style={{ background: 'forestgreen', color: 'white' }}>
                  {t('auth.reset.requestNewLink')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="card border-0 shadow-lg" style={{ borderRadius: '1rem' }}>
              <div className="card-body p-5 text-center">
                <div 
                  className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                  style={{ width: '70px', height: '70px', background: 'rgba(34, 139, 34, 0.1)' }}
                >
                  <i className="bi bi-check-circle" style={{ fontSize: '2rem', color: 'forestgreen' }}></i>
                </div>
                <h2 className="fw-bold mb-3">{t('auth.reset.done')}</h2>
                <p className="text-muted mb-4">
                  {t('auth.reset.doneText')}
                </p>
                <div className="spinner-border text-success" role="status">
                  <span className="visually-hidden">{t('auth.reset.redirecting')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          <div className="card border-0 shadow-lg" style={{ borderRadius: '1rem' }}>
            <div className="card-body p-5">
              <div className="text-center mb-4">
                <div 
                  className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{ width: '70px', height: '70px', background: 'rgba(34, 139, 34, 0.1)' }}
                >
                  <i className="bi bi-shield-lock" style={{ fontSize: '2rem', color: 'forestgreen' }}></i>
                </div>
                <h2 className="fw-bold mb-2">{t('auth.reset.setNew')}</h2>
                <p className="text-muted">
                  {t('auth.reset.setNewSubtitle')}
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="newPassword" className="form-label">{t('auth.reset.newPassword')}</label>
                  <input
                    id="newPassword"
                    type="password"
                    className="form-control form-control-lg"
                    placeholder={t('auth.reset.newPasswordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    autoFocus
                  />
                  {password.length > 0 && !passwordValid && (
                    <div className="form-text text-warning">
                      <i className="bi bi-exclamation-circle me-1"></i>
                      {t('auth.reset.errMinChars')}
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <label htmlFor="confirmPassword" className="form-label">{t('auth.reset.confirmPassword')}</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    className="form-control form-control-lg"
                    placeholder={t('auth.reset.confirmPasswordPlaceholder')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  {confirmPassword.length > 0 && !passwordsMatch && (
                    <div className="form-text text-warning">
                      <i className="bi bi-exclamation-circle me-1"></i>
                      {t('auth.reset.errMismatch')}
                    </div>
                  )}
                </div>

                {errorMessage !== null && (
                  <div className="alert alert-danger mb-4" role="alert">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {errorMessage}
                  </div>
                )}

                <div className="d-grid">
                  <button 
                    type="submit" 
                    className="btn btn-lg"
                    style={{ background: 'forestgreen', color: 'white' }}
                    disabled={isSubmitting || !passwordValid || !passwordsMatch}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        {t('auth.reset.resetting')}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg me-2"></i>
                        {t('auth.reset.resetButton')}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPassword

